import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GameType } from '../../types/games';
import restaurantService, { Restaurant } from '../../services/restaurantService';
import { gamesSignInPath } from '../../utils/gamesAuth';
import { useAppSelector } from '../../store/hooks';
import { useEnabledGames } from './useEnabledGames';
import SpinWheelGame from './games/SpinWheelGame';
import ScratchCardGame from './games/ScratchCardGame';
import SlotMachineGame from './games/SlotMachineGame';
import MysteryBoxGame from './games/MysteryBoxGame';
import DiceRollGame from './games/DiceRollGame';
import GuessNumberGame from './games/GuessNumberGame';
import StopClockGame from './games/StopClockGame';
import BillSplitGame from './games/BillSplitGame';
import GoogleReviewGame from './games/GoogleReviewGame';
import './GamePlay.css';

const VALID_GAME_TYPES: GameType[] = [
  'SPIN_WHEEL', 'SCRATCH_CARD', 'SLOT_MACHINE', 'MYSTERY_BOX',
  'DICE_ROLL', 'GUESS_NUMBER', 'STOP_CLOCK', 'BILL_SPLIT', 'GOOGLE_REVIEW',
];

const GamePlay: React.FC = () => {
  const { appName, gameType } = useParams<{ appName: string; gameType: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, authReady } = useAppSelector((state) => state.user);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const { enabledGameTypes, loading: enabledGamesLoading } = useEnabledGames(appName);

  useEffect(() => {
    if (!authReady || !appName || isAuthenticated) return;
    navigate(gamesSignInPath(appName, gameType), { replace: true });
  }, [authReady, isAuthenticated, appName, gameType, navigate]);

  useEffect(() => {
    if (!appName || !isAuthenticated) return;
    restaurantService.getRestaurant(appName)
      .then((res) => setRestaurant(res.restaurant))
      .catch(() => {});
  }, [appName, isAuthenticated]);

  function handleClose() {
    navigate(`/restaurants/${appName}/games`);
  }

  const gType = gameType as GameType;
  const isValid = VALID_GAME_TYPES.includes(gType);
  const isEnabled = enabledGameTypes.includes(gType);

  if (!authReady || !isAuthenticated || enabledGamesLoading) {
    return (
      <div className="gameplay-wrap">
        <div className="gameplay-not-found">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!isValid || !isEnabled) {
    return (
      <div className="gameplay-wrap">
        <div className="gameplay-not-found">
          <span style={{ fontSize: 36 }}>🎮</span>
          <p>{!isValid ? 'Game not found.' : 'This game is not available at this restaurant.'}</p>
          <button
            onClick={handleClose}
            style={{
              border: 'none',
              background: 'var(--primary, #F5C21A)',
              color: 'var(--text-on-primary, #1F2428)',
              fontWeight: 700,
              padding: '10px 20px',
              borderRadius: 10,
              cursor: 'pointer',
            }}
          >
            Back to Games
          </button>
        </div>
      </div>
    );
  }

  const outletId = restaurant?.id ?? 0;
  const tableSessionId = outletId;

  const sharedProps = { appName: appName!, outletId, onClose: handleClose };

  return (
    <div className="gameplay-wrap">
      {gType === 'SPIN_WHEEL' && <SpinWheelGame {...sharedProps} />}
      {gType === 'SCRATCH_CARD' && <ScratchCardGame {...sharedProps} />}
      {gType === 'SLOT_MACHINE' && <SlotMachineGame {...sharedProps} />}
      {gType === 'MYSTERY_BOX' && <MysteryBoxGame {...sharedProps} />}
      {gType === 'DICE_ROLL' && <DiceRollGame {...sharedProps} />}
      {gType === 'GUESS_NUMBER' && <GuessNumberGame {...sharedProps} />}
      {gType === 'STOP_CLOCK' && <StopClockGame {...sharedProps} />}
      {gType === 'BILL_SPLIT' && (
        <BillSplitGame
          appName={appName!}
          tableSessionId={tableSessionId}
          onClose={handleClose}
        />
      )}
      {gType === 'GOOGLE_REVIEW' && <GoogleReviewGame {...sharedProps} />}
    </div>
  );
};

export default GamePlay;
